
			<script lang="ts">
			import layout from '@/views/layouts/App/Main.vue'
			export default { layout }
			</script>
			<template>
		
    <Head>
        <title>{{ program.data.name }}</title>
        <meta name="description" :content="program.data.description" />
        <meta property="og:title" :content="program.data.name" />
        <meta property="og:description" :content="program.data.description" />
        <meta property="og:image" :content="program.data.cover_url" />
        <meta property="og:url" :content="route('app.programs.show', { program: program.data })" />
        <meta property="og:type" content="website" />
        <link
            key="canonical"
            rel="canonical"
            :href="route('app.programs.show', { program: program.data })"
        />
    </Head>
    <div class="pb-8">
        <div class="relative">
            <ProgramFeedAlert v-if="unreleasedWithFeed" class="my-6" :program="program" />

            <div class="absolute inset-x-0 bottom-0 h-1/2 bg-gray-100" />

            <div class="relative">
                <div class="absolute inset-0">
                    <img
                        class="h-full w-full object-cover"
                        :src="program.data.cover_url"
                        :alt="program.data.name"
                    />
                    <div class="absolute inset-0 bg-gray-400 mix-blend-multiply" />
                </div>

                <Container>
                    <div class="relative flex flex-col gap-6 py-16 sm:py-24 lg:pb-8 lg:pt-32">
                        <h1
                            class="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:mb-12 lg:text-6xl"
                        >
                            {{ program.data.name }}
                        </h1>
                        <div class="mt-10 max-w-sm sm:flex sm:max-w-none sm:justify-start">
                            <div class="grid gap-5 sm:grid-cols-2 sm:space-y-0 md:grid-cols-3">
                                <template v-if="loggedIn">
                                    <template v-if="unreleasedWithFeed">
                                        <Button
                                            size="xl"
                                            tag="link"
                                            :href="
                                                route('app.feeds.show', {
                                                    feed: program.data.feed!.id,
                                                })
                                            "
                                        >
                                            {{ trans('Submit Questions') }}
                                        </Button>
                                    </template>

                                    <template v-else>
                                        <Button
                                            v-if="
                                                program.data.feed?.is_active &&
                                                program.data.released
                                            "
                                            size="xl"
                                            tag="link"
                                            :href="
                                                route('app.feeds.show', {
                                                    feed: program.data.feed.id,
                                                })
                                            "
                                        >
                                            {{ trans('Join Show') }}
                                        </Button>
                                        <template v-else>
                                            <Button
                                                v-if="program.data.show_url"
                                                :href="
                                                    route('app.programs.watch', {
                                                        program: program.data,
                                                    })
                                                "
                                                :only="['modal', 'flash']"
                                                preserve-state
                                                preserve-scroll
                                                tag="link"
                                                size="xl"
                                                class="video-button"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 16 16"
                                                    fill="currentColor"
                                                    class="size-4 w-[25px]"
                                                >
                                                    <path
                                                        d="M3 3.732a1.5 1.5 0 0 1 2.305-1.265l6.706 4.267a1.5 1.5 0 0 1 0 2.531l-6.706 4.268A1.5 1.5 0 0 1 3 12.267V3.732Z"
                                                    />
                                                </svg>

                                                {{ trans('Start Show') }}
                                            </Button>
                                        </template>
                                    </template>
                                </template>

                                <Button
                                    v-if="program.data.trailer_url"
                                    :href="route('app.programs.trailer', { program: program.data })"
                                    tag="link"
                                    size="xl"
                                    variant="secondary"
                                    class="video-button"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        class="size-4"
                                    >
                                        <path
                                            d="M3 3.732a1.5 1.5 0 0 1 2.305-1.265l6.706 4.267a1.5 1.5 0 0 1 0 2.531l-6.706 4.268A1.5 1.5 0 0 1 3 12.267V3.732Z"
                                        />
                                    </svg>

                                    {{ trans('Watch Trailer') }}
                                </Button>

                                <div v-if="program.data.show_url" class="flex items-center">
                                    <WatchLater :id="program.data.id" type="programs" />
                                </div>
                            </div>
                        </div>
                        <div
                            class="mt-6 max-w-lg text-xl text-gray-300 sm:max-w-6xl"
                            v-html="program.data.description"
                        ></div>
                    </div>

                    <BookmarksDropdown
                        :id="program.data.id"
                        :base-url="route('app.programs.show', { program: program.data })"
                        type="programs"
                        class="absolute right-5 top-5"
                    />
                </Container>
            </div>
        </div>

        <Container v-if="program.data.user_access && isTeacher" class="mt-10">
            <Alert
                :message="`${trans('You have temporary access to this Program until')} ${ymd(
                    program.data.user_access.access_until,
                )}.`"
                variant="info"
                shadow
            />
        </Container>

        <div v-if="!program.data.can.view" class="mt-10 bg-primary-50">
            <Container
                class="px-4 py-12 sm:px-6 lg:flex lg:items-center lg:justify-between lg:px-8 lg:py-24"
            >
                <h2 class="text-3xl font-extrabold tracking-tight text-gray-900 md:text-4xl">
                    <span class="block">{{ trans('Ready to dive in?') }}</span>
                    <span class="block text-primary-600">
                        {{ trans('Start your free trial today.') }}
                    </span>
                </h2>

                <div class="mt-8 flex gap-x-4 lg:mt-0 lg:flex-shrink-0">
                    <Button :href="route('app.checkout.show')" size="lg" tag="link">
                        {{ trans('Get Live It') }}
                    </Button>

                    <Button
                        size="lg"
                        variant="secondary"
                        tag="a"
                        href="https://www.liveit.earth/how-live-it-earth-works"
                    >
                        {{ trans('Learn More') }}
                    </Button>
                </div>
            </Container>
        </div>

        <Container v-if="program.data.state === 'published'" class="mt-10">
            <div v-if="program.data.challenges?.length" class="mb-20">
                <h2 class="mb-8 text-3xl font-extrabold tracking-tight sm:text-4xl">
                    {{ trans('Challenges') }}
                </h2>

                <Carousel
                    :snap-align="snapAlign"
                    :breakpoints="breakpoints"
                    :class="program.data.challenges?.length < 4 ? 'shortCarousel' : ''"
                >
                    <Slide v-for="challenge in program.data.challenges" :key="challenge.id">
                        <div class="flex w-full flex-col gap-y-3">
                            <Card
                                :url="
                                    route('app.programs.challenges.show', {
                                        program: program.data,
                                        challenge: challenge,
                                    })
                                "
                                :thumbnail-url="challenge.thumbnail_url"
                                :alt="challenge.name"
                            />

                            <h3 class="text-left text-lg font-medium leading-6">
                                {{ challenge.name }}
                            </h3>
                        </div>
                    </Slide>

                    <template #addons>
                        <Navigation />
                    </template>
                </Carousel>
            </div>

            <div v-if="program.data.explore?.length" class="mb-20">
                <h2 class="mb-8 text-3xl font-extrabold tracking-tight sm:text-4xl">
                    {{ trans('Explore') }}
                </h2>

                <Carousel
                    :snap-align="snapAlign"
                    :breakpoints="breakpoints"
                    :class="program.data.explore?.length < 4 ? 'shortCarousel' : ''"
                >
                    <Slide v-for="resource in program.data.explore" :key="resource.id">
                        <div class="flex w-full flex-col gap-y-3">
                            <Card
                                :url="
                                    route('app.programs.resources.show', {
                                        program: program.data,
                                        resource: resource,
                                    })
                                "
                                :thumbnail-url="resource.thumbnail_url"
                                :alt="resource.name"
                            />

                            <h3 class="text-left text-lg font-medium leading-6">
                                {{ resource.name }}
                            </h3>
                        </div>
                    </Slide>

                    <template #addons>
                        <Navigation />
                    </template>
                </Carousel>
            </div>

            <div v-if="program.data.resources?.length" class="mb-20">
                <h2 class="mb-8 text-3xl font-extrabold tracking-tight sm:text-4xl">
                    {{ trans('Resources') }}
                </h2>

                <Carousel
                    :snap-align="snapAlign"
                    :breakpoints="breakpoints"
                    :class="program.data.resources?.length < 4 ? 'shortCarousel' : ''"
                >
                    <Slide v-for="resource in program.data.resources" :key="resource.id">
                        <div class="flex w-full flex-col gap-y-3">
                            <Card
                                :url="
                                    route('app.programs.resources.show', {
                                        program: program.data,
                                        resource: resource,
                                    })
                                "
                                :thumbnail-url="resource.thumbnail_url"
                                :alt="resource.name"
                            />

                            <h3 class="text-left text-lg font-medium leading-6">
                                {{ resource.name }}
                            </h3>
                        </div>
                    </Slide>

                    <template #addons>
                        <Navigation />
                    </template>
                </Carousel>
            </div>
        </Container>
        <Container v-if="isTeacher && !isFrench" class="mt-10">
            <div class="mb-20">
                <div class="mb-8">
                    <h2 class="text-3xl font-extrabold tracking-tight sm:text-4xl">
                        {{ trans('Curricular Connections') }}
                    </h2>

                    <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
                        Your results are based your personal
                        <Anchor :href="route('app.user.settings.index')" tag="link">
                            grade and subject settings
                        </Anchor>
                        .
                    </p>
                    <button
                        class="focus:ring-none relative mt-3 inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:px-8"
                        @click="openPopup"
                    >
                        See your connections
                    </button>
                </div>

                <div>
                    <div
                        v-if="showPopup"
                        class="fixed inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50"
                        @click.self="closePopup"
                    >
                        <div class="h-[94vh] overflow-y-scroll rounded-lg bg-white p-6">
                            <button class="float-right text-2xl" @click="closePopup">x</button>
                            <h2 class="mb-4 text-3xl font-extrabold tracking-tight sm:text-4xl">
                                Curriculum Connections
                            </h2>
                            <div class="flex flex-col items-start">
                                <div
                                    v-for="grade in curriculum"
                                    :key="grade.id"
                                    class="flex flex-col items-start gap-y-6"
                                >
                                    <div
                                        class="flex flex-col items-center border-b-4 border-solid border-primary-600"
                                    >
                                        <span>Grade</span>
                                        <h3
                                            class="text-3xl font-semibold tracking-tight sm:text-4xl"
                                        >
                                            {{ grade.level }}
                                        </h3>
                                        <div className="p-4 bg-primary"></div>
                                    </div>
                                    <div
                                        v-for="subject in grade.subjects"
                                        :key="subject.subject"
                                        class="flex w-[50rem] flex-row gap-x-6"
                                    >
                                        <div class="flex">
                                            <div class="flex flex-col items-center">
                                                <span>Subject</span>
                                                <h3
                                                    class="text-xl font-semibold tracking-tight sm:text-2xl"
                                                >
                                                    {{ subject.subject }}
                                                </h3>
                                            </div>
                                        </div>
                                        <div
                                            class="ml-auto max-w-5xl flex-1 divide-y rounded-lg bg-white shadow"
                                        >
                                            <Disclosure
                                                v-for="bigIdeaGradeSubject in subject.big_idea_grade_subjects"
                                                v-slot="{ open }"
                                                :key="bigIdeaGradeSubject.big_idea_id"
                                            >
                                                <DisclosureButton
                                                    class="flex w-full items-center justify-between gap-x-4 px-6 py-4 text-left text-sm font-medium"
                                                >
                                                    <h4 class="text-lg font-medium tracking-tight">
                                                        {{
                                                            bigIdeaGradeSubject.big_idea.description
                                                        }}
                                                    </h4>
                                                    <AsyncIcon
                                                        :class="open ? 'rotate-180 transform' : ''"
                                                        name="chevron-up"
                                                        class="h-5 w-5"
                                                    />
                                                </DisclosureButton>
                                                <Transition
                                                    enter-active-class="transition duration-50 ease-out"
                                                    enter-from-class="transform scale-95 opacity-0"
                                                    enter-to-class="transform scale-100 opacity-100"
                                                    leave-active-class="transition duration-25 ease-out"
                                                    leave-from-class="transform scale-100 opacity-100"
                                                    leave-to-class="transform scale-95 opacity-0"
                                                >
                                                    <DisclosurePanel class="p-4 text-sm">
                                                        <div
                                                            v-if="
                                                                bigIdeaGradeSubject.big_idea
                                                                    .elaboration
                                                            "
                                                            class="prose"
                                                        >
                                                            <h4>More about this Big Idea</h4>
                                                            <p>
                                                                {{
                                                                    bigIdeaGradeSubject.big_idea
                                                                        .elaboration
                                                                }}
                                                            </p>
                                                        </div>
                                                        <div
                                                            v-if="
                                                                bigIdeaGradeSubject.big_idea
                                                                    .competencies
                                                            "
                                                            class="prose"
                                                        >
                                                            <h4>Curricular Connection</h4>
                                                            <ul>
                                                                <li
                                                                    v-for="competency in bigIdeaGradeSubject
                                                                        .big_idea.competencies"
                                                                    :key="competency.sid"
                                                                >
                                                                    <span
                                                                        v-if="
                                                                            competency.elaboration
                                                                        "
                                                                        className="cursor-pointer"
                                                                        @click="
                                                                            showElaborationContent(
                                                                                competency.sid,
                                                                                'elaborations',
                                                                            )
                                                                        "
                                                                    >
                                                                        {{ competency.description }}
                                                                        <span
                                                                            v-if="
                                                                                elaborationContent ===
                                                                                competency.sid +
                                                                                    '-' +
                                                                                    'elaborations'
                                                                            "
                                                                        >
                                                                            &#11205;
                                                                        </span>
                                                                        <span v-else>&#11206;</span>
                                                                    </span>
                                                                    <span v-else>
                                                                        {{ competency.description }}
                                                                    </span>
                                                                    <span
                                                                        v-show="
                                                                            elaborationContent ===
                                                                            competency.sid +
                                                                                '-' +
                                                                                'elaborations'
                                                                        "
                                                                        className="block italic"
                                                                    >
                                                                        {{ competency.elaboration }}
                                                                    </span>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        <div
                                                            v-if="
                                                                bigIdeaGradeSubject.big_idea
                                                                    .contents
                                                            "
                                                            class="prose"
                                                        >
                                                            <h4>Content</h4>
                                                            <ul>
                                                                <li
                                                                    v-for="content in bigIdeaGradeSubject
                                                                        .big_idea.contents"
                                                                    :key="content.sid"
                                                                >
                                                                    {{ content.description }}
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        <div class="prose">
                                                            <h3>
                                                                Compare these competencies in
                                                                another program
                                                            </h3>
                                                            <ul>
                                                                <li
                                                                    v-for="programs in bigIdeaGradeSubject
                                                                        .big_idea.programs"
                                                                    :key="programs.id"
                                                                >
                                                                    <a
                                                                        :href="`/en/programs/${programs.slug}`"
                                                                        class="font-medium text-primary-600 hover:text-primary-500"
                                                                        tag="link"
                                                                    >
                                                                        {{ programs.name }}
                                                                    </a>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </DisclosurePanel>
                                                </Transition>
                                            </Disclosure>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    </div>

    <CouponForm v-if="!program.data.user_access" :program="program" />
</template>

<script setup lang="ts">
    import { ModelResource, ProgramModel, CurriculumModel } from '@/scripts/types/models'
    import { ref } from '@vue/runtime-core'
    import { Carousel, Slide, Navigation } from 'vue3-carousel'
    import { useSpacetime } from '@/scripts/hooks/useSpacetime'
    import { useCarousel } from '@/scripts/hooks/useCarousel'
    import { useAuth } from '@/scripts/hooks/useAuth'
    import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
    import CouponForm from '@/views/partials/Coupons/CouponForm.vue'
    import ProgramFeedAlert from '@/views/pages/App/Programs/Partials/ProgramFeedAlert.vue'
    import BookmarksDropdown from '@/views/partials/Bookmarks/BookmarksDropdown.vue'
    import Card from '@/views/partials/Common/Card.vue'
    import WatchLater from '@/views/partials/Bookmarks/WatchLater.vue'
    import { Head } from '@inertiajs/vue3'
    import { useLocales } from '@/scripts/hooks/useLocales'
    import Button from '@/views/components/Button.vue'
    import Container from '@/views/components/Layout/Container.vue'
    import Alert from '@/views/components/Alert.vue'
    import Anchor from '@/views/components/Anchor.vue'

    const props = defineProps({
        program: {
            type: Object as () => ModelResource<ProgramModel>,
            required: true,
        },
        curriculum: {
            type: Object as () => ModelResource<CurriculumModel>,
            required: true,
        },
    })

    const { ymd } = useSpacetime()
    const { breakpoints, snapAlign } = useCarousel()
    const { loggedIn, isTeacher } = useAuth()
    const showPopup = ref<boolean>(false)
    const { currentLocale } = useLocales()

    const isFrench = currentLocale.value.slug === 'fr'

    const unreleasedWithFeed = ref<boolean>(
        !props.program.data.released &&
            props.program.data?.release_at !== null &&
            (props.program.data.feed?.is_active ?? false),
    )

    const openPopup = () => {
        showPopup.value = true
    }

    const closePopup = () => {
        showPopup.value = false
    }

    const elaborationContent = ref('')
    const showElaborationContent = (sid: number, content: string) => {
        if (elaborationContent.value === `${sid}-${content}`) {
            elaborationContent.value = ''
        } else {
            elaborationContent.value = `${sid}-${content}`
        }
    }
</script>
